import { useMemo, useState } from "react";
import { filter, get } from "lodash";
import { Facet } from "@linkfluence/models/facet";
import { useCurrentProject } from "@radarly/core/project/use-cases";
import { useCurrentWorkspaceId } from "@radarly/core/workspace/use-cases";
import { Card } from "@radarly/models/cards";
import {
  getColorFromTerm,
  isGeoCardDataviz,
} from "@radarly/models/cards/dataviz";
import { getTermFromCard } from "@radarly/models/cards/term";
import { Clause } from "@radarly/models/clause";
import {
  getDateFromClause,
  getFieldFromInnerClause,
} from "@radarly/models/clause/getter";
import { isInnerClauseEmpty } from "@radarly/models/clause/is";
import { isCloudDataviz, isKpiDataviz } from "@radarly/models/dataviz";
import {
  PasteID,
  PastedCard,
  PastedData,
  PastedFilter,
  getCopiedDataKey,
} from "@radarly/models/insight-pages/editor";

export default function usePasteModalData(
  type: PasteID,
  onApply: (data: Card | Clause) => void
) {
  const project = useCurrentProject();
  const workspaceId = useCurrentWorkspaceId();
  const [selectedIndex, setSelectedIndex] = useState<number>(null);
  const data: PastedData = JSON.parse(
    localStorage.getItem(getCopiedDataKey(project.id, workspaceId))
  );

  const getPayload = () => {
    if (type === "paste_card") {
      return get(data, "cards")[selectedIndex];
    }
    return get(data, "filters")[selectedIndex].clause;
  };

  const onValidate = () => {
    const payload = getPayload();
    onApply(payload);
  };

  const formatInnerClauses = (clause: Clause) => {
    const validInnerClauses = filter(clause.and, (innerClause) => {
      const field = Object.keys(innerClause).toString();
      return (
        field !== "date" &&
        field !== "dashboard" &&
        !isInnerClauseEmpty(innerClause)
      );
    });
    return validInnerClauses.map((filter) => {
      const field = getFieldFromInnerClause(filter);
      return {
        field,
        clause: { and: [filter] },
      };
    });
  };

  const formattedFilters: PastedFilter[] = useMemo(() => {
    const visibleFilters = get(data, "filters");
    return visibleFilters.map((visibleFilter) => {
      return {
        cardTitle: visibleFilter.cardTitle,
        period: getDateFromClause(visibleFilter.clause),
        clauses: formatInnerClauses(visibleFilter.clause),
      };
    });
  }, [data]);

  const getCardVizColor = (card: Card) => {
    const term = getTermFromCard(card);
    return term ? getColorFromTerm(card.dataviz.name, term) : null;
  };

  const getCardVizOptions = (card: Card) => {
    const term = getTermFromCard(card);
    if (isKpiDataviz(card.dataviz)) {
      return { showLine: true };
    }
    if (isGeoCardDataviz(card.dataviz)) {
      return term !== Facet.Countries ? { [term]: true } : {};
    }
    if (isCloudDataviz(card.dataviz)) {
      return term === Facet.EmojiCharts ? { emojis: true } : {};
    }
    return {};
  };

  const formattedCards: PastedCard[] = useMemo(() => {
    const visibleCards = get(data, "cards");
    return visibleCards.map((card) => {
      return {
        ...card,
        color: getCardVizColor(card),
        vizOptions: getCardVizOptions(card),
      };
    });
  }, [data]);

  const allowValidation = useMemo(() => {
    if (type === "paste_card") {
      return formattedCards.length > 0;
    }
    return formattedFilters.length > 0;
  }, [formattedCards, formattedFilters, type]);

  return {
    filters: formattedFilters,
    cards: formattedCards,
    onSelect: (index: number) => setSelectedIndex(index),
    onValidate,
    selectedIndex,
    allowValidation,
  };
}
